import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { GeodataModel } from '../../../../../../typings/api/skymap/rest/v0/.common';
import { WebsocketMessage } from '../../../../../../typings/websocket-messages';
import { iiafe } from '../../../../utils/async';
import { WebsocketManager } from '../../../../utils/websocket/websocket-manager';
import { SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { useSubscribeCallback } from '../../../hooks/use-subscribe';
import { AppContext } from '../../../state/app-state';
import { isAccessTokenRedirect } from '../../../utils/access-token-redirect-flag';
import { GeodataImagesContext } from '../geodata-images-state';
import { GeodataContext } from '../geodata-state';
import { geodataStatuses } from '../use-geodata';
import { PlaceMarkersContext } from './place-markers-state';

export interface AlignerContext {
  state: State;
  setState: (state: State) => void;
  readyToQuickOptimize: boolean;
  userMoveFlagsStateEntered: Date;
  isLoading: boolean;
  legendsMode: LegendsMode;
  setLegendsMode: (mode: LegendsMode) => void;
  showOrganizationAdminModules: boolean;
  readyToOptimize: boolean;
}

const optimizeInstanceTimeoutMs = 15 * 60 * 1000; // 15 minutes
export const AlignerContext = React.createContext<AlignerContext>(undefined!);

type Props = {
  children: React.ReactNode;
};

type State =
  | 'add-images'
  | 'copy-existing'
  | 'optimize-legacy'
  | 'process-pending'
  | 'incompatible'
  | 'finished'
  | 'failed'
  | 'align-photos-failed'
  | 'aligner';

const getInitialState = (geodata: GeodataModel, isOrganizationAdmin: boolean): State => {
  if (geodata.isLegacyProcess) {
    return isOrganizationAdmin || geodata.status === 'user_move_flags'
      ? 'optimize-legacy'
      : 'incompatible';
  }

  if (geodata.photoCount === 0) {
    return 'add-images';
  }

  if (geodataStatuses.successStatuses.includes(geodata.status)) {
    return 'finished';
  }

  if (geodata.status === 'align_photos_failed') {
    return 'align-photos-failed';
  }

  if (geodataStatuses.failedStatuses.includes(geodata.status)) {
    return 'failed';
  }

  if (geodataStatuses.pendingStatuses.includes(geodata.status)) {
    return 'process-pending';
  }

  return 'aligner';
};

function pingOptimizeInstance(geodataId: string, uploadedById: string) {
  WebsocketManager.instance.sendToUserId(
    {
      message: 'OptimizeInstancePing',
      geodataId,
    },
    uploadedById,
  );
}

export const legendsModes = ['default', 'accuracy', 'iso', 'shutterSpeed', 'aperture'] as const;
export type LegendsMode = (typeof legendsModes)[number];

export const AlignerState = (props: Props) => {
  const { isOrganizationAdmin } = useContext(AppContext);
  const showOrganizationAdminModules = useMemo(
    () => isOrganizationAdmin && !isAccessTokenRedirect(),
    [isOrganizationAdmin],
  );
  const [legendsMode, setLegendsMode] = useState<LegendsMode>('default');
  const { geodata, isGeodataProcessing, isPending } = useContext(GeodataContext);
  const { images, progress, isPendingGetImages } = useContext(GeodataImagesContext);
  const { isPendingMarkers } = useContext(PlaceMarkersContext);
  const [readyToQuickOptimize, setReadyToQuickOptimize] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [userMoveFlagsStateEntered, setUserMoveFlagsStateEntered] = useState<Date>(new Date());

  const hasImages = useMemo(
    () => geodata.photoCount > 0 || images.filter((x) => x.status === 'COMPLETED').length > 2,
    [geodata.photoCount, images],
  );
  const isLegacyProcess = useMemo(() => geodata.isLegacyProcess, [geodata.isLegacyProcess]);
  const isProcessPending = useMemo(
    () => geodataStatuses.pendingStatuses.includes(geodata.status),
    [geodata.status],
  );
  const isGeodataFinished = useMemo(
    () => geodataStatuses.successStatuses.includes(geodata.status),
    [geodata.status],
  );
  const isGeodataFailed = useMemo(
    () => geodataStatuses.failedStatuses.includes(geodata.status),
    [geodata.status],
  );
  const isLoading = useMemo(
    () =>
      isPendingMarkers ||
      isGeodataProcessing ||
      isPendingGetImages ||
      progress.state === 'pending' ||
      isPending,
    [isPendingMarkers, isGeodataProcessing, isPendingGetImages, progress.state, isPending],
  );

  const [state, setState] = useState<State>(getInitialState(geodata, showOrganizationAdminModules));

  const readyToOptimize = useMemo(
    () =>
      showOrganizationAdminModules
        ? geodataStatuses.idleStatuses.includes(geodata.status)
        : geodataStatuses.readyToOptimizeStatuses.includes(geodata.status),
    [geodata.status, showOrganizationAdminModules],
  );

  const contextValue = useMemo<AlignerContext>(() => {
    return {
      state,
      setState,
      readyToQuickOptimize,
      userMoveFlagsStateEntered,
      isLoading,
      legendsMode,
      setLegendsMode,
      showOrganizationAdminModules,
      readyToOptimize,
    };
  }, [
    state,
    setState,
    readyToQuickOptimize,
    userMoveFlagsStateEntered,
    isLoading,
    legendsMode,
    setLegendsMode,
    showOrganizationAdminModules,
    readyToOptimize,
  ]);

  useSubscribeCallback(SubscriptionTopic.WebsocketMessageRecieved, (data: WebsocketMessage) => {
    const elapsedTimeMs = Date.now() - userMoveFlagsStateEntered.getTime();
    if (
      data.message === 'OptimizeInstancePong' &&
      data.geodataId === geodata.id &&
      elapsedTimeMs < optimizeInstanceTimeoutMs
    ) {
      setReadyToQuickOptimize(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Queue a new ping message in 30 seconds.
      timeoutRef.current = setTimeout(() => {
        pingOptimizeInstance(geodata.id, geodata.uploadedBy.id);
      }, 30000);
    }

    if (data.message === 'OptimizeInstanceDisconnected' && data.geodataId === geodata.id) {
      setReadyToQuickOptimize(false);
    }
  });

  const isInUserMoveFlagsState = useMemo(
    () => geodata.status === 'user_move_flags',
    [geodata.status],
  );

  // Ping once when loading the page if we are in the move flags state.
  useEffect(() => {
    if (isInUserMoveFlagsState) {
      iiafe(async () => {
        await WebsocketManager.instance.waitForWebsocketConnection();
        setUserMoveFlagsStateEntered(new Date());
        pingOptimizeInstance(geodata.id, geodata.uploadedBy.id);
      });
    }
  }, [isInUserMoveFlagsState, geodata.id, geodata.uploadedBy.id]);

  useEffect(() => {
    if (geodata.status === 'align_photos_failed') {
      setState('align-photos-failed');
      return;
    }

    // Automatically hide the dialog if the state allows it.
    if (
      hasImages &&
      !isLegacyProcess &&
      !isProcessPending &&
      !isGeodataFinished &&
      !isGeodataFailed
    ) {
      setState('aligner');
      return;
    }
  }, [
    geodata.status,
    hasImages,
    isLegacyProcess,
    isProcessPending,
    isGeodataFinished,
    isGeodataFailed,
  ]);

  return <AlignerContext.Provider value={contextValue}>{props.children}</AlignerContext.Provider>;
};
